import { useState, useEffect, useCallback } from 'react';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
}

type InstallPlatform = 'standard' | 'ios' | 'safari-desktop' | null;

// Capture the event globally so it's never missed — it fires once, often before React mounts
let deferredPrompt: BeforeInstallPromptEvent | null = null;

window.addEventListener('beforeinstallprompt', (e: Event) => {
  e.preventDefault();
  deferredPrompt = e as BeforeInstallPromptEvent;
});

window.addEventListener('appinstalled', () => {
  deferredPrompt = null;
});

function detectPlatform(): InstallPlatform {
  const ua = navigator.userAgent;
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  if (/^((?!chrome|android).)*safari/i.test(ua)) return 'safari-desktop';
  return 'standard';
}

function isStandalone(): boolean {
  if (window.matchMedia('(display-mode: standalone)').matches) return true;
  if ((navigator as unknown as Record<string, unknown>).standalone === true) return true;
  return false;
}

export function useInstallPrompt() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(deferredPrompt);
  const [platform] = useState<InstallPlatform>(() => detectPlatform());
  const [canShow, setCanShow] = useState(() => !isStandalone());

  useEffect(() => {
    if (isStandalone()) return;

    const handler = (e: Event) => {
      e.preventDefault();
      deferredPrompt = e as BeforeInstallPromptEvent;
      setInstallPrompt(deferredPrompt);
    };

    const installedHandler = () => {
      deferredPrompt = null;
      setInstallPrompt(null);
      setCanShow(false);
    };

    window.addEventListener('beforeinstallprompt', handler);
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  const triggerInstall = useCallback(async () => {
    if (installPrompt) {
      await installPrompt.prompt();
      deferredPrompt = null;
      setInstallPrompt(null);
      setCanShow(false);
    }
  }, [installPrompt]);

  return {
    canShow,
    canInstallDirectly: !!installPrompt,
    platform,
    triggerInstall,
  };
}
